import { useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import styled from '@emotion/styled';

import { Button, tokens, useNotify } from '@hotjar/hotsauce';
import { InfoBox } from '@hotjar/webapp/components/InfoBox/InfoBox';
import {
  PaymentIcon as Icon,
  Name,
  SUPPORTED_CARDS_ICONS,
} from '@hotjar/webapp/components/PaymentIcons/PaymentIcons';
import { moment } from '@hotjar/webapp/lib/moment';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { SubscriptionSummaryAccount } from '../SubscriptionSummary';
import * as S from '../SubscriptionSummary.styles';
import type { SubscriptionInfo } from '../SubscriptionNextInvoice/SubscriptionNextInvoice';
import { openDeletePaymentMethodModal } from './DeletePaymentMethodModal';

interface Props {
  subscriptionInfo: SubscriptionInfo;
  onDeletePayment: () => void;
  onUpdatePayment: (x: string) => void;
  account: SubscriptionSummaryAccount;
}

const Content = styled.div`
  display: flex;
`;

const SubText = styled.span<{ padding?: string }>`
  color: ${tokens.color.text_light};
  font-size: 12px;
  line-height: 140%;
  letter-spacing: 0.1px;
  padding: ${({ padding }) => padding || '0 0 0 42px'};
  white-space: nowrap;
`;

const FooterCTA = styled.div`
  display: flex;
`;

const ContentImages = styled.div`
  justify-content: space-between;
  align-items: stretch;
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 8px;
`;

const PaymentLabel = styled.span`
  padding-left: 8px;
`;

export const LegacySubscriptionPaymentDetails = ({
  subscriptionInfo,
  onDeletePayment,
  onUpdatePayment,
  account,
}: Props): JSX.Element => {
  const { showToast } = useNotify();
  const { t: tSummary } = useTranslationWithPrefix('billing:payment-details.summary');
  const { t: tDelete } = useTranslationWithPrefix('billing:payment-details.delete');
  const { t: tActions } = useTranslationWithPrefix('common:actions');
  const { click, getGroupDataForAccount } = useTrack();

  const handleEditPayment = useCallback(
    () => onUpdatePayment('Plans and Billing: Edit payment method button'),
    [onUpdatePayment],
  );

  const handleDeletePayment = async () => {
    click('Delete Payment Method', 'Plans and Billing Page', getGroupDataForAccount(account));
    try {
      await openDeletePaymentMethodModal(!!subscriptionInfo?.hasPaidPlan);
      onDeletePayment();
      showToast({
        text: tDelete('notify.success'),
        kind: 'info',
      });
    } catch (error) {
      if (error) {
        Sentry.captureMessage(`Error deleting payment method: ${error}`);
        Sentry.captureException(error);
        showToast({
          text: tDelete('notify.failure'),
          kind: 'danger',
        });
      }
    }
  };

  const handleAddPayment = useCallback(
    () => onUpdatePayment('Plans and Billing: Add payment method button'),
    [onUpdatePayment],
  );

  const subscriptionPaymentMethod = subscriptionInfo?.paymentMethod;
  const subscriptionNextBilling = subscriptionInfo?.nextBillingDate;

  const paymentType = subscriptionPaymentMethod?.type;
  const paymentLabel = subscriptionPaymentMethod?.label;
  const cardExpiryDate = subscriptionPaymentMethod?.expiryDate;
  const paymentAvailable = subscriptionPaymentMethod?.available;

  const subscriptionAlerts = subscriptionInfo?.alerts;
  const isErrorInfo = subscriptionAlerts.trialEndingSoon;

  const renderPaymentSection = () => {
    if (isErrorInfo) {
      return (
        <S.ContentSection>
          <S.ErrorInfoTitle>
            <Icon name={Name.warning} alt="alert" /> {tSummary('no-payment-method')}
          </S.ErrorInfoTitle>
          <S.ErrorInfoContent>
            {tSummary('add-payment-method-warning', {
              date: moment.unix(subscriptionNextBilling).format('Do MMMM YYYY'),
            })}
          </S.ErrorInfoContent>
        </S.ContentSection>
      );
    }
    if (paymentAvailable && paymentType === 'credit_card') {
      return (
        <S.ContentSection>
          <Content>
            <Icon name={Name.default} alt="credit-card" />
            <PaymentLabel>•••• •••• •••• {paymentLabel}</PaymentLabel>
          </Content>
          <SubText>
            {tSummary('card-expires', { date: moment.unix(cardExpiryDate).format('MM/YY') })}
          </SubText>
        </S.ContentSection>
      );
    }
    if (paymentAvailable && paymentType === 'paypal') {
      return (
        <S.ContentSection>
          <Content>
            <Icon name={Name.paypal} alt="paypal" />
            <PaymentLabel>{paymentLabel}</PaymentLabel>
          </Content>
          <SubText>{tSummary('paypal')}</SubText>
        </S.ContentSection>
      );
    }
    if (!paymentAvailable) {
      return (
        <S.ContentSection>
          <ContentImages>
            {SUPPORTED_CARDS_ICONS.map((payment) => (
              <Icon key={payment} name={payment} alt={payment} />
            ))}
          </ContentImages>

          <SubText padding="0">{tSummary('accepted-methods')}</SubText>
        </S.ContentSection>
      );
    }
    return null;
  };

  const renderFooterCTAs = (): JSX.Element | null => {
    if (account && account.lock) {
      return null;
    }
    if (isErrorInfo) {
      return (
        <Button id="btn-set-payment-method" size="s" onClick={handleAddPayment}>
          {tSummary('add-payment-method')}
        </Button>
      );
    }
    if (!paymentAvailable) {
      return (
        <S.SwitchLink id="btn-set-payment-method" onClick={handleAddPayment} variant="primary">
          {tSummary('add-payment-method')}
        </S.SwitchLink>
      );
    }
    return (
      <FooterCTA data-testid="payment-ctas">
        <S.SwitchLink id="btn-update-payment-method" onClick={handleEditPayment} variant="primary">
          {tActions('edit')}
        </S.SwitchLink>
        <S.SwitchLink
          id="btn-delete-payment-method"
          onClick={handleDeletePayment}
          variant="secondary"
        >
          {tActions('delete')}
        </S.SwitchLink>
      </FooterCTA>
    );
  };

  return (
    <InfoBox
      title={<S.Header>{tSummary('payment-method')}</S.Header>}
      body={renderPaymentSection()}
      footer={renderFooterCTAs()}
      isErrorInfo={isErrorInfo}
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default LegacySubscriptionPaymentDetails;
